import { defineRouter } from "#q-app"
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
  type LocationQueryValue,
} from "vue-router"
import { AUTH_TOKEN_QUERY_PARAM } from "src/app/opsSystem"
import { useAuthStore } from "src/stores/auth"
import { setLoginScreen } from "src/stores/theme"
import { PARAM_RETURN_PATH_FULL, setAppRouter } from "./appRouter"
import routes from "./routes"

function firstQueryValue(
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): string | null {
  if (Array.isArray(value)) return value[0] ?? null
  return value ?? null
}

export default defineRouter(function (/* { store, ssrContext } */) {
  const createHistory = import.meta.env.QUASAR_SERVER
    ? createMemoryHistory
    : import.meta.env.QUASAR_VUE_ROUTER_MODE === "history"
      ? createWebHistory
      : createWebHashHistory

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,

    // Mode and base belong to `quasar.config.ts` (build → vueRouterMode / publicPath), not here.
    history: createHistory(import.meta.env.QUASAR_VUE_ROUTER_BASE),
  })

  setAppRouter(Router)

  // Auth guard.
  Router.beforeEach(async (to, from) => {
    const auth = useAuthStore()

    // A newer frontend has been deployed: a route change is a safe point to pick it up,
    // so do a full-page load to the target — the user lands on the fresh bundle where they intended
    // to go. Skip the initial navigation (no `from.name`) and same-page query/hash changes (e.g.
    // filter updates persisted to the URL), which would otherwise reload mid-interaction.
    if (auth.newVersionAvailable && from.name != null && to.path !== from.path) {
      window.location.assign(Router.resolve(to.fullPath).href)
      return false
    }

    // User is coming back from the IdP. The backend returns the token in the URL *fragment* (never sent
    // to the server, so it stays out of access logs / Referer), appended to the original URL the user was
    // on. Consume it from the hash and strip it; re-running the guard on the cleaned location picks up the
    // rest of the flow (Login → returnPath, auth checks). The token is gone, so we won't loop back here.
    const hashParams = new URLSearchParams(to.hash.replace(/^#/, ""))
    const authToken = hashParams.get(AUTH_TOKEN_QUERY_PARAM)
    if (authToken) {
      await auth.saveLogin(authToken)
      return { path: to.path, query: to.query, hash: "" }
    }

    if (auth.authenticated === null) {
      // try to restore a saved login
      await auth.loadLogin()
    }

    if (auth.authenticated === true && to.name === "Login") {
      // authenticated but on the login page → redirect away
      const returnPathFull = firstQueryValue(to.query[PARAM_RETURN_PATH_FULL])
      return returnPathFull ? returnPathFull : { name: "Start" }
    }

    if (to.meta.public === true) {
      return true // public route, no auth needed
    }

    if (auth.authenticated !== true) {
      if (auth.lockedOut) {
        return false // session-lock overlay is showing — cancel navigation
      }
      return { name: "Login", query: { [PARAM_RETURN_PATH_FULL]: to.fullPath } }
    }

    return true
  })

  // `afterEach`, so it sees the route actually landed on rather than a `beforeEach` target that may
  // still redirect.
  Router.afterEach((to) => setLoginScreen(to.meta.public === true))

  return Router
})
